import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './FilterPopup.module.css';
import { useIsDesktop } from '../../hooks/useMediaQuery';

const POPOVER_WIDTH = 380;
const VIEWPORT_MARGIN = 12;
const POPOVER_GAP = 8;

interface FilterPopupProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onApply?: () => void;
  showApplyButton?: boolean;
  /**
   * Optional anchor element. When provided AND viewport is desktop (>=1024px),
   * the popup renders as a positioned popover anchored below the element.
   * Otherwise (mobile, or no anchor) renders as bottom sheet — original behavior.
   */
  anchorEl?: HTMLElement | null;
  children: React.ReactNode;
}

interface PopoverPosition {
  left: number;
  top: number;
}

function computePosition(anchorEl: HTMLElement): PopoverPosition {
  const rect = anchorEl.getBoundingClientRect();
  const viewportWidth = window.innerWidth;

  // Default: right-align popover with anchor's right edge
  let left = rect.right - POPOVER_WIDTH;

  // Left edge collision: align with anchor's left edge instead
  if (left < VIEWPORT_MARGIN) {
    left = rect.left;
  }

  // Right edge collision: clamp inside viewport
  if (left + POPOVER_WIDTH > viewportWidth - VIEWPORT_MARGIN) {
    left = viewportWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
  }

  // Also clamp left at minimum margin (safety net for very small viewports)
  if (left < VIEWPORT_MARGIN) {
    left = VIEWPORT_MARGIN;
  }

  const top = rect.bottom + POPOVER_GAP;

  return { left, top };
}

export function FilterPopup({
  open,
  onClose,
  title,
  onApply,
  showApplyButton = false,
  anchorEl,
  children,
}: FilterPopupProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const isDesktop = useIsDesktop();
  const usePopover = isDesktop && !!anchorEl;
  const [position, setPosition] = useState<PopoverPosition | null>(null);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    // In popover mode (desktop with anchor) we do NOT lock body scroll —
    // the popover floats above the page and reposition handlers handle scroll.
    if (usePopover) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, usePopover]);

  // Compute popover position when in popover mode, and update on resize/scroll.
  useLayoutEffect(() => {
    if (!open || !usePopover || !anchorEl) {
      setPosition(null);
      return;
    }

    const update = () => {
      setPosition(computePosition(anchorEl));
    };

    update();

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true, capture: true });

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, { capture: true } as EventListenerOptions);
    };
  }, [open, usePopover, anchorEl]);

  // Outside-click close in popover mode (no backdrop scrim).
  useEffect(() => {
    if (!open || !usePopover) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      // Ignore clicks inside the popover itself.
      if (sheetRef.current && sheetRef.current.contains(target)) return;
      // Ignore clicks on the anchor element (the trigger should toggle, not double-fire).
      if (anchorEl && anchorEl.contains(target)) return;
      onClose();
    };

    // Use mousedown so we fire before the trigger's onClick (avoids reopen race).
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, usePopover, anchorEl, onClose]);

  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      const timer = setTimeout(() => {
        const firstFocusable = sheetRef.current?.querySelector<HTMLElement>(
          'button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();
      }, 100);
      return () => clearTimeout(timer);
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [open]);

  const backdropClass = `${styles.backdrop} ${open ? styles.backdropVisible : ''}`.trim();
  const containerClass = usePopover
    ? `${styles.popoverVariant} ${open ? styles.popoverVariantOpen : ''}`.trim()
    : `${styles.sheet} ${open ? styles.sheetOpen : ''}`.trim();

  const containerStyle: React.CSSProperties | undefined =
    usePopover && position
      ? { left: `${position.left}px`, top: `${position.top}px` }
      : usePopover
        ? // Position not yet computed: keep off-screen to avoid flash at (0,0)
          { left: '-9999px', top: '-9999px' }
        : undefined;

  return createPortal(
    <>
      {/* In popover mode we omit the visible backdrop (outside-click handler closes instead). */}
      {!usePopover && <div className={backdropClass} onClick={onClose} aria-hidden="true" />}

      <div
        ref={sheetRef}
        className={containerClass}
        style={containerStyle}
        data-variant={usePopover ? 'popover' : 'sheet'}
        role="dialog"
        aria-label={title}
        aria-modal="true"
        aria-hidden={!open}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
        </div>

        <div className={styles.body}>{children}</div>

        {showApplyButton && onApply && (
          <div className={styles.footer}>
            <button type="button" className={styles.applyBtn} onClick={onApply}>
              Filtra
            </button>
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
